import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthContext from "../store/authContext";
import OrgContext from "../store/orgContext";
import useAppTitle from "../hooks/useAppTitle";
import useHideSidePanel from "../hooks/useHideSidePanel";
import {
  DELETE_IKI_CONNECTION_TRANSLATIONS,
  READ_IKI_CONNECTION_TRANSLATIONS,
} from "../translations/util";
import ESOganisationDropdown from "../components/dropdown/esOrganisationDropdown";
import SectionHeader from "../components/section/SectionHeader";
import AdvancedSearch from "../components/advancedSearch/AdvancedSearch";
import SearchContext from "../store/searchContext";
import {
  createSearchConfig,
  downloadCertificate,
  errorAlert,
  makeApiRequest,
  successAlert,
  transformIKIResponseRow,
  transformIKIRows,
  transformIKIUpdateBody,
} from "../util";
import { ADD_IKI_CONNECTION_FORM_FIELDS } from "../constants/addFormFields";
import DataTableWithControls from "../components/dataTable/DataTableWithControls";
import { IKI_FILTER_COLUMNS } from "../constants/filterColumns";
import TableActionButton from "../ui/TableActionButton";
import { IKI_TABLE_HEADERS } from "../constants/tableHeaders";
import InfoAlert from "../ui/InfoAlert";
import EditRow from "../components/dataTable/EditRow";
import { INITIAL_IKI_EDIT_STATE } from "../constants/editState";
import { EDIT_IKI_CONNECTION_FORM_FIELDS } from "../constants/editFormFields";
import {
  deleteIKIConnection,
  getIKIConnectionByRef,
  getIWFCertificate,
  updateIKIConnectionByRef,
} from "../api";

interface ISearchData {
  "CONNECTION-ID"?: string | null;
  "IKI-IPV4-ADDR"?: string | null;
  "IKI-PORT"?: string | null;
  "IKI-NAME"?: string | null;
}

const INITIAL_STATE = {
  "CONNECTION-ID": "",
  "IKI-IPV4-ADDR": "",
  "IKI-PORT": null,
  "IKI-NAME": "",
};

const searchFields = ["CONNECTION-ID", "IKI-IPV4-ADDR", "IKI-PORT", "IKI-NAME"];

const searchConfigNew = createSearchConfig(
  searchFields,
  ADD_IKI_CONNECTION_FORM_FIELDS
);

const IKIConnections: React.FC = () => {
  const {
    token,
    scopes: { iki_connections: ikiConnections },
  } = useContext(AuthContext);
  const { orgs: orgList, selectedOrg, updateOrg } = useContext(OrgContext);
  const { selectedColumn, setSelectedColumn, setSearchText, setDisableSearch } =
    useContext(SearchContext);

  useAppTitle("IKI Connections- IWF Config Admin Tool (ICAT) by AT&T");

  const navigate = useNavigate();

  // Custom hook to handle side-panel open and close
  const {
    showSidePanel,
    showAdvancedSearch,
    handleToggleAdvanceSearchPanel,
    handleToggleSidePanel,
  } = useHideSidePanel();

  // State to hold selected IKI Connection-ID
  const [selectedConnectionId, setSelectedConnectionId] = useState<
    string | null
  >(null);

  // State to hold searched string
  const [searchData, setSearchData] = useState<ISearchData>(INITIAL_STATE);

  // State to hold rows related data
  const [numberOfRows, setNumberOfRows] = useState(0);
  const [numberOfPages, setNumberOfPages] = useState(0);

  const [loading, setLoading] = useState(false);

  // State to hold search query
  const [searchQuery, setSearhQuery] = useState<{
    data: ISearchData;
    pageNum: number;
    pageSize: number;
    sortByField?: string;
    sortDirection?: "desc" | "asc" | undefined;
  }>({
    pageSize: 20,
    pageNum: 1,
    data: {},
    sortByField: undefined,
    sortDirection: undefined,
  });

  const [IKIConnections, setIKIConnections] = useState<
    {
      "CONNECTION-ID": string;
      "IKI-IPV4-ADDR": string;
      "IWF-RADIO-SET-IDENTIFIER": number;
      "IKI-RADIO-SET-IDENTIFIER": number;
      "IKI-PORT": number;
      "IKI-NAME": string;
    }[]
  >([]);

  const fetchIKIConnections = async () => {
    setLoading(true);
    try {
      if (selectedOrg) {
        const { data, pageNum, pageSize, sortByField, sortDirection } =
          searchQuery;
        const queryParams = new URLSearchParams({
          page: `${pageNum}`,
          pageSize: `${pageSize}`,
          ...(sortByField && { sortByField }),
          ...(sortDirection && { sortDirection }),
          ...(data &&
            data["CONNECTION-ID"] && {
              "CONNECTION-ID": data["CONNECTION-ID"],
            }),
          ...(data &&
            data["IKI-IPV4-ADDR"] && {
              "IKI-IPV4-ADDR": data["IKI-IPV4-ADDR"],
            }),
          ...(data &&
            data["IKI-PORT"] && {
              "IKI-PORT": data["IKI-PORT"],
            }),
          ...(data &&
            data["IKI-NAME"] && {
              "IKI-NAME": data["IKI-NAME"],
            }),
        });
        const response = await makeApiRequest<{
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          Rows: Array<any>;
          NbrOfRows: number;
          NbrOfPages: number;
        }>({
          path: `/api/v1/app-config/iki-connections?${queryParams}`,
          token,
          org: selectedOrg.id,
        });

        // To hide IWF Certificate in listing page ok IKI Connections
        const normalRows = response.Rows.filter(
          (row) =>
            row["IKI-NAME"] !== "IWF" &&
            row["CONNECTION-ID"] !== "Connection-IWF"
        );
        setNumberOfRows(normalRows.length);
        setNumberOfPages(response.NbrOfPages);
        setIKIConnections(normalRows);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadIWFCertificate = async () => {
    let certificate;
    try {
      if (selectedOrg && token) {
        const response = await getIWFCertificate({
          org: selectedOrg.id,
          token,
        });
        certificate = response.Rows.filter(
          (row: { [x: string]: string }) =>
            row["IKI-NAME"] === "IWF" &&
            row["CONNECTION-ID"] === "Connection-IWF"
        );

        downloadCertificate(
          certificate[0]["IKI-CERTIFICATE"],
          "IWF_Certificate"
        );
      }
    } catch (error) {
      errorAlert(error);
    }
    return certificate;
  };

  useEffect(() => {
    setSelectedColumn(null);
    setSearchText("");
    setDisableSearch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSearchData(INITIAL_STATE);
    setSearhQuery({
      pageSize: 20,
      pageNum: 1,
      data: {},
      sortByField: undefined,
      sortDirection: undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg]);

  useEffect(() => {
    fetchIKIConnections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg, searchQuery]);

  useEffect(() => {
    if (!showAdvancedSearch && Object.keys(searchQuery.data).length === 0) {
      setSearchData(INITIAL_STATE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAdvancedSearch]);

  // State to hold sorting directions
  const sortTable = (col: string, direction: "asc" | "desc") => () => {
    setSearhQuery({
      ...searchQuery,
      sortByField: col,
      sortDirection: direction,
    });
  };

  // This finction will delete the selected IKI connection record
  const deleteConnection = async (ref: string) => {
    if (selectedOrg && token) {
      try {
        await deleteIKIConnection({
          token,
          org: selectedOrg.id,
          ref,
        });
        successAlert(DELETE_IKI_CONNECTION_TRANSLATIONS.successAlertMessage);
        handleToggleSidePanel();
        setSelectedConnectionId(null);
        fetchIKIConnections();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        errorAlert(e);
      }
    }
  };

  return (
    <>
      <SectionHeader
        pageTitle={READ_IKI_CONNECTION_TRANSLATIONS.header}
        pageDescription={READ_IKI_CONNECTION_TRANSLATIONS.headerSubtext}
        sideComponent={
          <div className="dropdown-details">
            <ESOganisationDropdown
              org={selectedOrg || undefined}
              orgsList={orgList}
              setOrg={updateOrg}
            />
          </div>
        }
      />
      {selectedOrg ? (
        <>
          {showAdvancedSearch ? (
            <AdvancedSearch
              setShowAdvancedSearch={handleToggleAdvanceSearchPanel}
              advancedSearchState={searchData}
              advancedSearchConfig={searchConfigNew}
              setAdvancedSearchState={setSearchData}
              onSubmit={(data) =>
                setSearhQuery({
                  ...searchQuery,
                  pageNum: 1,
                  pageSize: 20,
                  data,
                })
              }
              onCancel={() => {
                setSearchData(INITIAL_STATE);
                handleToggleAdvanceSearchPanel();
              }}
            />
          ) : null}

          <DataTableWithControls
            loading={loading}
            filterColumns={IKI_FILTER_COLUMNS}
            selectedColumn={selectedColumn}
            setSelectedColumn={setSelectedColumn}
            onSearch={(term) =>
              setSearhQuery({
                ...searchQuery,
                pageNum: 1,
                pageSize: 20,
                data: { [`${selectedColumn?.id}`]: term },
              })
            }
            setShowAdvancedSearch={handleToggleAdvanceSearchPanel}
            showCreateButton={ikiConnections && ikiConnections.includes("c")}
            onCreateButtonClick={() => {
              navigate(`/configurationManagement/ikiconnections/new`);
            }}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            rows={transformIKIRows(IKIConnections).map((row: any) => ({
              ...row,
              action: (
                <TableActionButton
                  onActionButtonClick={() => {
                    setSelectedConnectionId(row.id);
                    handleToggleSidePanel();
                  }}
                />
              ),
            }))}
            numberOfAdvancedSearchFilters={searchConfigNew.reduce(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (acc, el: any) => {
                const { name } = el;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const obj: any = { ...searchData };
                return acc + (obj[name] && obj[name].length > 0 ? 1 : 0);
              },
              0
            )}
            clearAdvancedSearchFilters={() => {
              setSearchData(INITIAL_STATE);
              setSearhQuery({
                ...searchQuery,
                pageNum: 1,
                pageSize: 20,
                data: {},
              });
            }}
            headers={IKI_TABLE_HEADERS}
            pageNum={searchQuery.pageNum}
            setPageNum={(pageNum) => setSearhQuery({ ...searchQuery, pageNum })}
            totalItems={numberOfRows}
            totalPages={numberOfPages}
            pageSize={searchQuery.pageSize}
            setPageSize={(pageSize) =>
              setSearhQuery({ ...searchQuery, pageSize })
            }
            sortByField={searchQuery.sortByField}
            sortDirection={searchQuery.sortDirection}
            handleSort={sortTable}
            showIKICertificateDownloadButton
            onDownloadButtonClick={handleDownloadIWFCertificate}
          />

          {showSidePanel && selectedOrg && selectedConnectionId ? (
            <EditRow
              panelName={READ_IKI_CONNECTION_TRANSLATIONS.editRow.panelName}
              initialState={INITIAL_IKI_EDIT_STATE}
              org={selectedOrg.id}
              esRef={selectedConnectionId}
              setShowEditPanel={handleToggleSidePanel}
              onDelete={() => deleteConnection(selectedConnectionId)}
              formFields={EDIT_IKI_CONNECTION_FORM_FIELDS}
              header={READ_IKI_CONNECTION_TRANSLATIONS.editRow.header}
              transformResponseRow={transformIKIResponseRow}
              transformUpdateBody={transformIKIUpdateBody}
              onUpdate={updateIKIConnectionByRef}
              loadDataByRef={getIKIConnectionByRef}
              hasDeletePermission={
                ikiConnections && ikiConnections.includes("d")
              }
              hasEditPermission={ikiConnections && ikiConnections.includes("u")}
              refreshTable={fetchIKIConnections}
              deleteModalItemName="CONNECTION-ID"
              showSmallSidepanel
            />
          ) : null}
        </>
      ) : (
        <InfoAlert
          heading={READ_IKI_CONNECTION_TRANSLATIONS.infoAlert.heading}
          subText={READ_IKI_CONNECTION_TRANSLATIONS.infoAlert.subText}
        />
      )}
    </>
  );
};

export default IKIConnections;
